/**
 * Чисті правила єдиного формату системних повідомлень (див. Ship/Helpers/notify):
 * текст, тривалість, ключ для склеювання дублікатів і черга з обмеженням кількості.
 */

export type NotifyType = 'success' | 'error' | 'info' | 'warning'

/** Скільки секунд показувати повідомлення; false — висить до закриття */
export const NOTIFY_DURATION: Record<NotifyType, number | false> = {
    success: 4,
    info: 6,
    warning: 6,
    error: false,
}

/** Скільки повідомлень одночасно тримати в стосі; старші закриваються */
export const NOTIFY_MAX_COUNT = 5

/** Типові заголовки, які нічого не кажуть і лише дублюють колір/іконку */
const GENERIC_TITLES = new Set(
    ['успіх', 'успішно', 'помилка', 'ошибка', 'увага', 'інформація', 'success', 'error', 'warning', 'info'].map(
        (title) => title.toLowerCase()
    )
)

/** Текст за замовчуванням, коли викликали без жодного тексту */
const FALLBACK_TITLE: Record<NotifyType, string> = {
    success: 'Готово',
    error: 'Сталася помилка. Спробуйте ще раз.',
    info: 'Інформація',
    warning: 'Увага',
}

const clean = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

export const isGenericTitle = (title: unknown): boolean => GENERIC_TITLES.has(clean(title).toLowerCase())

/**
 * Один рядок замість «Успіх / Налаштування збережено»: якщо заголовок типовий, а опис є,
 * опис стає заголовком. Опис, що повторює заголовок, прибирається.
 */
export const normalizeNotifyContent = (
    type: NotifyType,
    title: unknown,
    description?: unknown
): { title: string; description?: string } => {
    let head = clean(title)
    let body = clean(description)

    if (body && (head === '' || isGenericTitle(head))) {
        head = body
        body = ''
    }
    if (head === '') head = FALLBACK_TITLE[type]
    if (body === head) body = ''

    return body ? { title: head, description: body } : { title: head }
}

/** Ключ для однакових повідомлень: повторне не додається в стос, а оновлює наявне */
export const buildNotifyKey = (type: NotifyType, title: string, description?: string): string =>
    `as-notify:${type}:${title}:${description ?? ''}`

/**
 * Черга відкритих повідомлень. push повертає ключі, які треба закрити, щоб у стосі
 * лишилося не більше max; повторний ключ переноситься в кінець (найновіший).
 */
export class NotifyQueue {
    private keys: string[] = []
    private readonly max: number

    constructor(max: number = NOTIFY_MAX_COUNT) {
        this.max = max
    }

    push(key: string): string[] {
        this.keys = this.keys.filter((existing) => existing !== key)
        this.keys.push(key)
        const overflow = this.keys.length > this.max ? this.keys.slice(0, this.keys.length - this.max) : []
        this.keys = this.keys.slice(this.keys.length - Math.min(this.keys.length, this.max))
        return overflow
    }

    remove(key: string): void {
        this.keys = this.keys.filter((existing) => existing !== key)
    }

    clear(): void {
        this.keys = []
    }

    get size(): number {
        return this.keys.length
    }
}
